"use client";

import type { EmblaCarouselType } from "embla-carousel";
import { useEffect, useState } from "react";

export type IndicatorTransition =
  | "fadeIn"
  | "swipeFadeIn"
  | "fadeOut"
  | "scrollHide";

export type IndicatorVisibility = {
  visible: boolean;
  transition: IndicatorTransition;
};

export const INDICATOR_TIMING = {
  landingDelayMs: 300,
  scrollStopMs: 150,
  showAfterScrollStopMs: 450,
  holdMs: 3500,
} as const;

/** 写真がこの割合以上画面内にあるときを「見えている」とみなす */
const INDICATOR_IN_VIEW_RATIO = 0.6;

/** 指が触れた程度の縦移動でバーを消さないよう、これ未満の移動はスクロールとみなさない */
export const INDICATOR_SCROLL_THRESHOLD_PX = 80;

const INITIAL_INDICATOR_VISIBILITY: IndicatorVisibility = {
  visible: false,
  transition: "fadeOut",
};

/**
 * ギャラリーのインジケーターを「きっかけがあった時だけ出し、hold 後に消す」状態遷移。
 * React から切り離し、fake timers で遷移を検証できるようにしている。
 */
export function createIndicatorVisibilityController(
  onChange: (visibility: IndicatorVisibility) => void,
  initialScrollY: number,
) {
  let current = INITIAL_INDICATOR_VISIBILITY;
  // しきい値判定の基準。静止するたびにその位置へ更新する
  let scrollOriginY = initialScrollY;
  let lastScrollY = initialScrollY;
  let inView = false;
  // 「前回静止時に見えていたカード」はスクロール停止後に再表示しない
  let inViewAtLastRest = false;
  let landed = false;
  let scrolling = false;
  let dragging = false;
  let showTimer: ReturnType<typeof setTimeout> | undefined;
  let scrollStopTimer: ReturnType<typeof setTimeout> | undefined;
  let smallScrollStopTimer: ReturnType<typeof setTimeout> | undefined;
  let holdTimer: ReturnType<typeof setTimeout> | undefined;

  const emit = (next: IndicatorVisibility) => {
    if (
      next.visible === current.visible &&
      next.transition === current.transition
    ) {
      return;
    }
    current = next;
    onChange(next);
  };

  const scheduleHold = () => {
    clearTimeout(holdTimer);
    // ドラッグ中に消えないよう、hold は settle 時に開始する
    if (dragging) return;
    holdTimer = setTimeout(
      () => emit({ visible: false, transition: "fadeOut" }),
      INDICATOR_TIMING.holdMs,
    );
  };

  const settleRest = () => {
    const shouldShow = inView && !inViewAtLastRest;
    inViewAtLastRest = inView;
    if (!shouldShow) return;
    if (!current.visible) emit({ visible: true, transition: "fadeIn" });
    scheduleHold();
  };

  return {
    /** 初回の交差判定はページ着地として扱う */
    intersect(nextInView: boolean) {
      inView = nextInView;
      if (landed) return;
      landed = true;
      if (scrolling || !nextInView) return;
      showTimer = setTimeout(settleRest, INDICATOR_TIMING.landingDelayMs);
    },
    scroll(scrollY: number) {
      lastScrollY = scrollY;
      if (!scrolling) {
        clearTimeout(smallScrollStopTimer);
        // しきい値未満のまま止まった場合はスクロールが無かった扱いにし、表示状態やタイマーに触れない
        if (Math.abs(scrollY - scrollOriginY) < INDICATOR_SCROLL_THRESHOLD_PX) {
          smallScrollStopTimer = setTimeout(() => {
            scrollOriginY = lastScrollY;
          }, INDICATOR_TIMING.scrollStopMs);
          return;
        }
        scrolling = true;
        dragging = false;
        clearTimeout(showTimer);
        clearTimeout(holdTimer);
        if (current.visible) emit({ visible: false, transition: "scrollHide" });
      }
      clearTimeout(scrollStopTimer);
      scrollStopTimer = setTimeout(() => {
        scrolling = false;
        scrollOriginY = lastScrollY;
        showTimer = setTimeout(
          settleRest,
          INDICATOR_TIMING.showAfterScrollStopMs,
        );
      }, INDICATOR_TIMING.scrollStopMs);
    },
    carouselScroll() {
      if (scrolling) return;
      dragging = true;
      clearTimeout(holdTimer);
      if (!current.visible) emit({ visible: true, transition: "swipeFadeIn" });
    },
    carouselSettle() {
      if (!dragging) return;
      dragging = false;
      if (current.visible) scheduleHold();
    },
    dispose() {
      clearTimeout(showTimer);
      clearTimeout(scrollStopTimer);
      clearTimeout(smallScrollStopTimer);
      clearTimeout(holdTimer);
    },
  };
}

/** カードの一覧はページ本体（window）でスクロールするため window の scroll を監視する */
export function useIndicatorVisibility(
  emblaApi: EmblaCarouselType | undefined,
  enabled: boolean,
) {
  const [visibility, setVisibility] = useState(INITIAL_INDICATOR_VISIBILITY);

  useEffect(() => {
    if (!emblaApi || !enabled) return;
    const controller = createIndicatorVisibilityController(
      setVisibility,
      window.scrollY,
    );

    const handleScroll = () => controller.scroll(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.at(-1);
        if (!entry) return;
        controller.intersect(
          entry.isIntersecting &&
            entry.intersectionRatio >= INDICATOR_IN_VIEW_RATIO,
        );
      },
      { threshold: INDICATOR_IN_VIEW_RATIO },
    );
    observer.observe(emblaApi.rootNode());

    const handleCarouselScroll = () => controller.carouselScroll();
    const handleCarouselSettle = () => controller.carouselSettle();
    emblaApi.on("scroll", handleCarouselScroll);
    emblaApi.on("settle", handleCarouselSettle);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
      emblaApi.off("scroll", handleCarouselScroll);
      emblaApi.off("settle", handleCarouselSettle);
      controller.dispose();
    };
  }, [emblaApi, enabled]);

  return visibility;
}
